import {
  combineLatestTyped,
  mergeTyped,
  useDataStream,
  useStreamValue,
  useVoidEventAsStream
} from 'annotation-tools/utils';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { map, mapTo } from 'rxjs/operators';
import { createUser } from '../../../api/fetch-functions/user';
import { UserAPIRequestBodyType } from '../../../api/types/user/api-request-body';
import { maxStringLength } from '../../../constants/max-string-length';
import { viewTexts } from '../../../constants/view-texts';
import { useFetchResultState } from '../../../functions/hooks/use-fetch-result-state';
import {
  FormInputStateConfigType,
  useFormInputState
} from '../../../functions/hooks/use-form-input-state';
import { useToken } from '../../../global-state/auth-state';
import { fetchUserList } from '../../../global-state/database/user-list';
import { IUserType } from '../../../types/record/user';
import { CreateNewUserDialogView } from './create-new-user-dialog-view';

const usernameInputStateConfig: FormInputStateConfigType<UserAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.username,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.userName,
  required: true,
  validationErrorContentMapFn: content =>
    content.name !== undefined ? content.name.toArray() : []
};

const emailInputStateConfig: FormInputStateConfigType<UserAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.email,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.emailAddress,
  required: true,
  validationErrorContentMapFn: content =>
    content.email !== undefined ? content.email.toArray() : []
};

const passwordInputStateConfig: FormInputStateConfigType<UserAPIRequestBodyType> = {
  minStringLength: 1,
  maxStringLength: maxStringLength.password,
  stringLengthCheckMessageTargetName:
    viewTexts.stringLengthCheckMessageTargetName.password,
  required: true,
  validationErrorContentMapFn: content =>
    content.password !== undefined ? content.password.toArray() : []
};

interface Props {
  open: boolean;
  onClose: (reason: 'ok' | 'cancel' | '') => void;
}

export const CreateNewUserDialog: FC<Props> = memo(
  ({ open, onClose }: Readonly<Props>) => {
    const token = useToken();

    /* state */

    const [
      // dummy comment
      fetchResult,
      setFetchResult
    ] = useFetchResultState<IUserType, UserAPIRequestBodyType>();

    const nonFieldErrors = useMemo<string[]>(
      () =>
        fetchResult !== undefined &&
        fetchResult.type === 'error' &&
        fetchResult.body.type === 'non-field-errors'
          ? fetchResult.body.content.toArray()
          : [],
      [fetchResult]
    );

    const [
      username$,
      usernameErrors$,
      usernameIsValid$,
      usernameOnInput
    ] = useFormInputState<IUserType, UserAPIRequestBodyType>(
      open,
      fetchResult,
      usernameInputStateConfig
    );

    const [
      email$,
      emailErrors$,
      emailIsValid$,
      emailOnInput
    ] = useFormInputState<IUserType, UserAPIRequestBodyType>(
      open,
      fetchResult,
      emailInputStateConfig
    );

    const [
      password$,
      passwordErrors$,
      passwordIsValid$,
      passwordOnInput
    ] = useFormInputState<IUserType, UserAPIRequestBodyType>(
      open,
      fetchResult,
      passwordInputStateConfig
    );

    const [isActive, onActiveFlagChange] = useState<boolean>(true);
    useEffect(() => {
      onActiveFlagChange(true);
    }, [open]);

    const [submitOnClick$, submitOnClick] = useVoidEventAsStream();

    const submitButtonEnabled$ = useDataStream<boolean>(
      false,
      mergeTyped(
        submitOnClick$.pipe(mapTo(false)),
        combineLatestTyped(
          usernameIsValid$,
          emailIsValid$,
          passwordIsValid$
        ).pipe(
          map(
            ([usernameIsValid, emailIsValid, passwordIsValid]) =>
              usernameIsValid && emailIsValid && passwordIsValid
          )
        )
      )
    );

    /* extract values */

    const submitButtonEnabled = useStreamValue(submitButtonEnabled$, false);

    const username = useStreamValue(username$);
    const usernameErrors = useStreamValue(usernameErrors$);
    const email = useStreamValue(email$);
    const emailErrors = useStreamValue(emailErrors$);
    const password = useStreamValue(password$);
    const passwordErrors = useStreamValue(passwordErrors$);

    /* event handler */

    const closeOnClick = useCallback(
      (reason: '' | 'cancel' | 'ok') => {
        if (reason !== 'ok') {
          onClose(reason);
        } else {
          submitOnClick();
          if (
            token !== undefined &&
            username !== undefined &&
            email !== undefined &&
            password !== undefined
          ) {
            createUser(token)({
              name: username,
              email,
              password,
              isActive
            }).then(res => {
              setFetchResult(res);
              if (res.type === 'success') {
                onClose(reason);
                fetchUserList();
              }
            });
          }
        }
      },
      [
        onClose,
        username,
        email,
        password,
        isActive,
        token,
        setFetchResult,
        submitOnClick
      ]
    );

    return (
      <CreateNewUserDialogView
        open={open}
        onClose={closeOnClick}
        username={username}
        usernameErrors={usernameErrors}
        usernameOnInput={usernameOnInput}
        email={email}
        emailErrors={emailErrors}
        emailOnInput={emailOnInput}
        password={password}
        passwordErrors={passwordErrors}
        passwordOnInput={passwordOnInput}
        isActive={isActive}
        onActiveFlagChange={onActiveFlagChange}
        submitButtonEnabled={submitButtonEnabled}
        nonFieldErrors={nonFieldErrors}
      />
    );
  }
);

CreateNewUserDialog.displayName = 'CreateNewUserDialog';
